/**
 * AINativeProvider — React context for BuildOS API access and auth state.
 *
 * Provides:
 *  - API base URL (configurable via VITE_API_URL env var)
 *  - Auth token state (stored in sessionStorage)
 *  - Typed API client (fetch wrapper)
 *  - useAINative() hook — full context
 *  - useAuth() hook — auth slice
 *
 * Token is attached as `Authorization: Bearer <token>` on all API calls.
 * Unauthenticated users see a login screen; the shell gates on token presence.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UserClaims {
  sub: string;
  name?: string;
  email?: string;
  provider?: string;
  iat?: number;
  exp?: number;
  [key: string]: unknown;
}

export interface AuthState {
  /** Raw JWT token, or null if not authenticated. */
  token: string | null;
  /** Decoded claims from /me, populated after successful login. */
  user: UserClaims | null;
  /** True while /me is being fetched after a token is set. */
  loading: boolean;
  /** Last auth error message, if any. */
  error: string | null;
}

export interface DevLoginOptions {
  sub: string;
  name?: string;
  email?: string;
}

export interface AuthActions {
  /** Sign in via POST /auth/dev-login (local dev path). */
  devLogin(opts: DevLoginOptions): Promise<void>;
  /** Sign out — clears the token. */
  signOut(): void;
}

/** Typed results for the most common API calls. */
export interface ApiCallOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
}

export interface ApiClient {
  /** Authenticated fetch — throws on non-2xx. */
  call<T = unknown>(path: string, opts?: ApiCallOptions): Promise<T>;
  /** GET /me — returns the session's JWT claims. */
  getMe(): Promise<{ claims: UserClaims }>;
  /** GET /db/tables — proxied ZeroDB tables list. */
  getDbTables(): Promise<unknown>;
}

export interface AINativeContextValue {
  /** API base URL, e.g. http://localhost:3001 */
  apiUrl: string;
  auth: AuthState;
  authActions: AuthActions;
  api: ApiClient;
}

// ─── Storage key ─────────────────────────────────────────────────────────────

const TOKEN_STORAGE_KEY = "buildos_token";

// ─── Context ─────────────────────────────────────────────────────────────────

const AINativeContext = createContext<AINativeContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export interface AINativeProviderProps {
  /**
   * Override the API base URL. Defaults to the VITE_API_URL env var
   * or `http://localhost:3001` if not set.
   */
  apiUrl?: string;
  children: React.ReactNode;
}

export function AINativeProvider({ apiUrl: apiUrlProp, children }: AINativeProviderProps) {
  const apiUrl =
    apiUrlProp ??
    // VITE_API_URL is injected by Vite at build time; fall back for test envs.
    (import.meta.env["VITE_API_URL"] as string | undefined) ??
    "http://localhost:3001";

  const [token, setToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const [user, setUser] = useState<UserClaims | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // ── API client ─────────────────────────────────────────────────────────────

  const api = useMemo<ApiClient>(() => {
    async function call<T = unknown>(path: string, opts: ApiCallOptions = {}): Promise<T> {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(opts.headers ?? {}),
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiUrl}${path}`, {
        method: opts.method ?? "GET",
        headers,
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`API ${opts.method ?? "GET"} ${path} failed: ${response.status} ${text}`);
      }

      return response.json() as Promise<T>;
    }

    return {
      call,
      getMe() {
        return call<{ claims: UserClaims }>("/me");
      },
      getDbTables() {
        return call<unknown>("/db/tables");
      },
    };
  }, [apiUrl, token]);

  // ── Fetch /me when token changes ───────────────────────────────────────────

  useEffect(() => {
    if (!token) {
      setUser(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .getMe()
      .then((data) => {
        if (!cancelled) {
          setUser(data.claims);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : "Failed to fetch user";
          setError(msg);
          setLoading(false);
          // Token may be expired / invalid — clear it
          setToken(null);
          try {
            sessionStorage.removeItem(TOKEN_STORAGE_KEY);
          } catch {
            // ignore
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token, api]);

  // ── Auth actions ───────────────────────────────────────────────────────────

  const devLogin = useCallback(
    async (opts: DevLoginOptions) => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${apiUrl}/auth/dev-login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(opts),
        });

        if (!response.ok) {
          const text = await response.text().catch(() => response.statusText);
          throw new Error(`Dev login failed: ${response.status} ${text}`);
        }

        const data = (await response.json()) as { token: string };
        const newToken = data.token;

        try {
          sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        } catch {
          // sessionStorage may be unavailable (e.g. private browsing in some browsers)
        }

        setToken(newToken);
        // loading will be cleared by the /me fetch effect
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Login failed";
        setError(msg);
        setLoading(false);
      }
    },
    [apiUrl],
  );

  const signOut = useCallback(() => {
    try {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
      // ignore
    }
    setToken(null);
    setUser(null);
    setError(null);
    setLoading(false);
  }, []);

  // ── Assembled context value ────────────────────────────────────────────────

  const value = useMemo<AINativeContextValue>(
    () => ({
      apiUrl,
      auth: { token, user, loading, error },
      authActions: { devLogin, signOut },
      api,
    }),
    [apiUrl, token, user, loading, error, devLogin, signOut, api],
  );

  return <AINativeContext.Provider value={value}>{children}</AINativeContext.Provider>;
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Returns the full AINative context value.
 * Must be used inside <AINativeProvider>.
 */
export function useAINative(): AINativeContextValue {
  const ctx = useContext(AINativeContext);
  if (!ctx) {
    throw new Error("useAINative must be used inside <AINativeProvider>");
  }
  return ctx;
}

/**
 * Returns just the auth state + actions slice.
 * Convenience hook for components that only need auth info.
 */
export function useAuth(): AuthState & AuthActions {
  const { auth, authActions } = useAINative();
  return { ...auth, ...authActions };
}
